<template>
	<div class="ui-table">
		<ui-progress v-if="isPending" />

		<ui-alert v-if="error" error>{{ error }}</ui-alert>

		<div class="table-container">
			<table class="table is-fullwidth is-hoverable is-striped mb-0">
				<thead style="white-space: nowrap;" class="no-select">
					<tr>
						<th v-for="header in headers" :key="header.value" :style="`text-align: ${header.align ? header.align : 'left'};`">
							<span v-if="header.sortable" class="pointer" @click="doSort(header.value)">{{ header.text }}
								<div class="ui-table-sortIcon">
									<i :class="getSortIconClass(header.value)"/>
								</div>
							</span>

							<span v-else>{{ header.text }}</span>
						</th>
					</tr>
				</thead>

				<tbody style="white-space: pre-line;" :class="[rowHoverEffect && 'row-hover-effect']">
					<tr v-for="item, index in items" :key="item[itemKey]" :style="getRowColor(index)" @click="$emit('rowClick', item)">
						<td v-for="header in headers" :key="header.value" :style="`text-align: ${header.align ? header.align : 'left'};`">
							<span v-if="header.transform">{{
								header.transform(
									$utils.dotProp(item, header.value, header.default || "")
								)
							}}</span>
							<span v-else-if="header.transformFromParentObject">
								{{ header.transformFromParentObject(item) }}
							</span>
							<span v-else>{{
								$utils.dotProp(item, header.value, header.default || "")
							}}</span>

							<slot :name="`item.${header.value}`" :item="item" />
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<!-- <div class="ui-table-bottomBorder mb-2"/> -->

		<ui-pagination :pagination="pagination" @on-update-page="updatePage" @on-update-pagesize="updatePageSize"/>
	</div>
</template>

<script>

export default {
	props: {
		queryParams: {
			type: Object,
			default: () => ({})
		},

		orderBy: {
			type: Object,
			default: () => ({})
		},

		headers: {
			type: Array,
			default: () => []
		},

		itemKey: {
			type: String,
			default: 'id'
		},

		service: {
			type: String,
			required: true
		},

		method: {
			type: String,
			required: true
		},

		rowTint: {
			type: String,
			default: 'white'
		},

		rowHoverEffect: {
			type: Boolean,
			default: false
		},

		activeTab: {
			type: String,
			required: false
		}
	},

	data: () => ({
		items: [],
		isPending: false,
		error: '',
		localQueryParams: {
			orderBy: 'id',
			search: ''
		},

		sorting: {},

		pagination: {
			totalCount: 0,
			pageSize: 10,
			currentPage: 1,
			totalPages: 0
		},

		currentTab: ''
	}),

	watch: {
		queryParams: {
			deep: true,
			handler(to) {
				this.localQueryParams = { ...to };
				this.fetchData();
			}
		}
	},

	created() {
		this.localQueryParams = { ...this.localQueryParams, ...this.queryParams };
		this.sorting = {...this.orderBy};
		this.fetchData();
	},

	methods: {
		getRowColor(index) {
			const even = index % 2 == 0;

			return even ? `background-color: rgb(${this.rowTint})`: `background-color: rgb(${this.rowTint} / 50%)`;
		},

		getSortIconClass(key) {
			const value = this.$utils.dotProp(this.sorting, key, null);

			if (!value) {
				return '';
			}

			return value.desc ? 'fas fa-chevron-down' : 'fas fa-chevron-up';
		},

		doSort(key) {
			if (!(key in this.sorting)) {
				this.$set(this.sorting, key, {
					desc: false
				});
			} else {
				if (!this.sorting[key].desc) {
					this.sorting[key].desc = true;
				} else {
					// eslint-disable-next-line no-unused-vars
					const { [key]: removedProperty, ...restObject } = this.sorting;
					this.sorting = restObject;
				}
			}

			this.localQueryParams.orderBy = Object.entries(this.sorting).reduce((acc, [key, value]) => {
				if (acc) {
					return acc + `, ${key}${value.desc ? ' desc' : ''}`;
				}

				return `${key}${value.desc ? ' desc' : ''}`;
			}, '');

			this.fetchData();
		},

		getItemText(header, item) {
			if (header.transform) {
				return header.transform(item);
			}

			return this.$utils.dotProp(item, header.value, '');
		},

		updatePage(pageNumber) {
			this.pagination.currentPage = pageNumber;
			this.fetchData();
			this.$emit('query-params-update', { ...this.localQueryParams });
		},

		updatePageSize(pageSize) {
			this.pagination.pageSize = pageSize;
			this.pagination.pageNumber = 1;
			this.fetchData();
			this.$emit('query-params-update', { ...this.localQueryParams });
		},

		doOrderBy(orderBy) {
			this.localQueryParams.orderBy = orderBy;
			this.fetchData();
			this.$emit('query-params-update', { ...this.localQueryParams });
		},

		async fetchData() {
			this.error = '';

			if (this.activeTab !== this.currentTab) {
				this.items = [];
				this.resetPagination();
			}

			this.currentTab = this.activeTab;

			try {
				const data = await this.$services[this.service][this.method](
					{
						...this.localQueryParams,
						...this.pagination
					}, {
						loading: val => {
							this.isPending = val;
						}
					});

				this.pagination.totalPages = data._pagination.totalPages;
				this.pagination.currentPage = data._pagination.currentPage;

				this.items = data;
			} catch (error) {
				this.error = error.message;
			}
		},

		resetPagination() {
			this.pagination = {
				totalCount: 0,
				pageSize: 10,
				currentPage: 1,
				totalPages: 0
			};
		}
	}
};
</script>

<style lang="scss">
@import '@/assets/colors.scss';

.ui-table {
	.table-container {
		overflow: auto;
		border-radius: 4px;
	}

	table {
		border-top-left-radius: 4px;
		border-top-right-radius: 4px;

		font-size: 16px;

		tbody.row-hover-effect {
			tr:hover {
				background: $list-background-hover !important;
				cursor: pointer;

				i {
					color: white;
				}
			}
		}
	}

	td {
		padding: 0.3em 0.75em !important;
	}
}

.ui-table-bottomBorder {
	background-color: white;
	height: 5px;
	border-bottom-left-radius: 4px;
	border-bottom-right-radius: 4px;

}

.ui-table-sortIcon {
	display: inline-block;
	width: 14px
}

.ui-table .pointer {
	cursor: pointer;
}
</style>
